import { useMemo, useCallback } from 'react';
import { isSameDay, getDay } from 'date-fns';
import { useBreakpointValue, useColorMode } from '@chakra-ui/react';

import { EventGridTile } from './EventGridTile';
import { EventPopover } from './event-popover';
import { Loader } from '../ui';
import { useCalendarSettings, useModal, useEventPositionContext, getEventColors } from '../../lib';

import type { IEvent } from '../../lib';

interface IEventProps {
	event: IEvent;
	continuesPrior?: boolean;
	continuesAfter?: boolean;
}

export const Event = ({ event, continuesPrior, continuesAfter }: IEventProps) => {
	const { colorMode } = useColorMode();
	const { openModal } = useModal();
	const { settings } = useCalendarSettings();
	const isDesktop = useBreakpointValue({ base: false, md: true });
	const { eventPositions } = useEventPositionContext();

	const isSameDayEvent = useMemo(() => isSameDay(event.start, event.end), [event]);

	const handleOpenModal = useCallback(() => {
		openModal({ event, isSameDayEvent });
	}, [openModal, event, isSameDayEvent]);

	if (!event || !settings?.formats) return <Loader />;

	const { showTooltip, enableFeaturedImage } = settings;

	const isSaturday = getDay(event.start) === 6;
	const ignoreDirection = isSaturday && !continuesPrior && continuesAfter;

	const { backgroundColor, textColor } = getEventColors(event, settings, colorMode);

	const marginTop =
		enableFeaturedImage && eventPositions[event.id] && isDesktop ? `-${eventPositions[event.id] - 2}px` : '0px';

	return (
		<EventPopover
			event={event}
			isSameDayEvent={isSameDayEvent}
			showTooltip={!!showTooltip}
			onOpenModal={handleOpenModal}
		>
			{(handlers) => (
				<EventGridTile
					event={event}
					backgroundColor={backgroundColor}
					textColor={textColor}
					isSameDayEvent={isSameDayEvent}
					ignoreDirection={ignoreDirection}
					continuesPrior={continuesPrior}
					marginTop={marginTop}
					{...handlers}
				/>
			)}
		</EventPopover>
	);
};
